import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import LoginSuccess from './LoginSuccess'
import PartnerSelect from './PartnerSelect'
import { useCognito, useCognitoDispatch } from './CognitoContextProvider'
import { challengeTypes } from './types'
import { FormPage } from './Shared/FormPage'
import { usePostLoginRedirect } from './hooks'
import InactiveUserModal from './Shared/InactiveUserModal'
import { getQueryParam } from 'utils/helpers'

const initialState = {
  email: '',
  password: '',
}

/**
 * Login form for the application (connected to Cognito AWS in the Backend).
 * @Tags( cognito, api )
 * @Inputs( email, password )
 * @Endpoints( /auth, /features )
 * @ApiLogic( ../../api/auth )
 */
const Form = ({ state, handleSubmit, t, handleChange }) => {
  const { email, password } = state
  const { error, loading } = useCognito()

  return (
    <>
      <form onSubmit={handleSubmit}>
        {error && !error.includes('error.auth.inactive') && (
          <p data-testid="errorForm" className="alert alert-danger">
            {t(error)}
          </p>
        )}
        <div className="form-group">
          <input
            data-cy="username-input-cy"
            type="email"
            className="form-control s-50"
            id="email"
            value={email}
            onChange={(e) => handleChange(e)}
            placeholder={t('common.email')}
            autoFocus
          />
        </div>
        <div className="form-group">
          <input
            data-cy="password-input-cy"
            type="password"
            className="form-control s-50"
            id="password"
            value={password}
            onChange={(e) => handleChange(e)}
            placeholder={t('common.password')}
          />
        </div>
        <div className="forgot-password">
          <Link data-cy="forgot-password-link-cy" to="/forgot-password">
            {t('Login.form.forgotPassword')}
          </Link>
        </div>
        <button
          data-cy="login-submit-button-cy"
          type="submit"
          className="bt bt-navy s-50"
          style={{ width: '100%' }}
          disabled={loading}
        >
          {loading ? t('Login.form.btnLoading') : t('Login.form.btn')}
        </button>
      </form>
      <InactiveUserModal />
    </>
  )
}

Form.propTypes = {
  state: PropTypes.object,
  handleSubmit: PropTypes.func,
  t: PropTypes.func,
  handleChange: PropTypes.func,
}

const LoginForm = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [state, setState] = useState(initialState)
  const { selectedPartner, setSelectedPartner, loginState, showSplash, setShowSplash } =
    usePostLoginRedirect()
  const user = useSelector((state) => state.user)
  const dispatch = useDispatch()
  const { challenge } = useCognito()
  const { authenticate } = useCognitoDispatch()

  const handleSubmit = useCallback(
    (event, email, password) => {
      event.preventDefault()

      authenticate({ username: email, password })
    },
    [authenticate],
  )

  useEffect(() => {
    const username = getQueryParam('username')
    const temporaryPassword = decodeURI(getQueryParam('tp'))
    if (username && temporaryPassword) {
      setState({ email: username, password: temporaryPassword })
      handleSubmit({ preventDefault: () => {} }, username, temporaryPassword)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (event) => {
    setState({ ...state, [event.target.id]: event.target.value })
  }

  useEffect(() => {
    if (!challenge) {
      return
    }

    if (
      challenge.name === challengeTypes.MFA_SETUP &&
      !user.mfaSettings?.softwareTokenMfa &&
      !user.mfaSettings?.emailTotp
    ) {
      history.push('/setup-mfa')
    } else if (challenge.name === challengeTypes.NEW_PASSWORD_REQUIRED) {
      history.push('/setpassword')
    } else if (
      [challengeTypes.SOFTWARE_TOKEN_MFA, challengeTypes.EMAIL_OTP].includes(challenge.name)
    ) {
      history.push('/mfa')
    }
  }, [challenge, user.mfaSettings, history])

  if (!user.isLogged) {
    return (
      <FormPage title={t('Login.form.title')} subtitle={t('Login.form.subtitle')}>
        <Form
          state={state}
          handleSubmit={(e) => handleSubmit(e, state.email, state.password)}
          t={t}
          handleChange={handleChange}
        />
      </FormPage>
    )
  } else if (selectedPartner || user.cognitoGroups?.length === 1) {
    return (
      <FormPage title={t('Login.form.title')}>
        <LoginSuccess state={loginState} showSplash={showSplash} setShowSplash={setShowSplash} />
      </FormPage>
    )
  } else {
    return (
      <FormPage
        title={t('Login.form.partnerSelectTitle')}
        subtitle={t('Login.form.partnerSelectSubtitle')}
      >
        <PartnerSelect
          setSelectedPartner={setSelectedPartner}
          dispatch={dispatch}
          cognitoGroups={user.cognitoGroups}
        />
      </FormPage>
    )
  }
}

export default LoginForm
